import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { KnowledgeBaseFiltersIcon } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { array, object, number } from 'yup';
import { Checkbox } from 'src/components/ui/checkbox';
import { useAppDispatch, useAppSelector } from 'src/store';
import { setFilteredTypes } from 'src/store/slices/knowledgeBaseSlices';

// A source type (integrations, files, FAQ, websites) picked in the filter.
interface FilterId {
  id: number;
}

export const FiltersKnowledgeBaseModal = ({ disabled = false }) => {
  const [open, setOpen] = useState(false);
  const { types, filteredTypes } = useAppSelector((s) => s.knowledgeBaseSlices);
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<{ filters_id: FilterId[] }>({
    defaultValues: {
      filters_id: filteredTypes,
    },
    resolver: formResolver(
      object({
        filters_id: array(object({ id: number() }))
          .required()
          .label('Filter'),
      }),
    ),
  });

  useEffect(() => {
    if (filteredTypes) {
      setValue('filters_id', filteredTypes);
    }
  }, [filteredTypes]);

  const checkedItems = watch('filters_id');

  const submit = (formValues: { filters_id: FilterId[] }) => {
    const filteredFilters = types.filter((item) =>
      formValues.filters_id.some((el) => el.id === item.id),
    );

    dispatch(setFilteredTypes({ types: filteredFilters }));
    setOpen(false);
  };

  const activateFiltersCheckBoxHandler = (
    checked: boolean | 'indeterminate',
    onChange: (ids: FilterId[]) => void,
    value: FilterId[],
    type: FilterId,
  ) => {
    checked
      ? onChange([...value, { id: Number(type.id) }])
      : onChange(value.filter((val) => val.id !== type.id));
  };

  return (
    <Dialog onOpenChange={setOpen} open={disabled ? false : open}>
      <DialogTrigger disabled={disabled} asChild>
        <Button
          variant="ghost"
          type="button"
          className="px-3.5 gap-2 bg-transparent border-[#E3E6E8] text-ebony_opacity_50 hover:text-ebony_opacity_80 hover:border-ebony_opacity_30 shadow-none"
        >
          <span>Filters</span>
          <KnowledgeBaseFiltersIcon />
        </Button>
      </DialogTrigger>
      <DialogContent closeButtonHidden>
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="content">
            <form
              id="filter-items"
              onSubmit={handleSubmit(submit)}
              className={'flex flex-col gap-[20px] max-h-[500px]'}
            >
              <div className="flex flex-col relative">
                <Controller
                  control={control}
                  name="filters_id"
                  render={({ field: { onChange, value } }) => (
                    <>
                      {types.map((type) => (
                        <div key={type.id} className="flex items-center space-x-5 border-b  w-full">
                          <label
                            htmlFor={`sub-${type.id}`}
                            className="py-6 w-full text-base flex items-center gap-2.5 text-ebony cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            <Checkbox
                              id={`sub-${type.id}`}
                              checked={value?.some((val) => val.id === type.id)}
                              onCheckedChange={(checked) => {
                                activateFiltersCheckBoxHandler(checked, onChange, value, type);
                              }}
                            />

                            {type?.title}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                />
                {errors?.filters_id?.message && (
                  <p className="text-sm font-semibold text-error absolute top-full mt-3">
                    {errors.filters_id.message}
                  </p>
                )}
              </div>
              <div className="flex justify-center">
                <div className="flex gap-2.5">
                  <Button
                    variant="outline"
                    type="button"
                    className="min-w-24"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!isDirty || !checkedItems.length}
                    type="submit"
                    className="min-w-24"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
